import { ReactNode, useCallback, useMemo } from 'react';

import dayjs from 'dayjs';

import { useCluster } from '@cast/shared-cluster-context-data-access';
import {
  prepareWorkload,
  useWorkloadScalingPolicies,
} from 'features/workload-optimization';
import { useWorkloadsAccessContextQuery } from 'hooks/queries/workload-optimization';
import { fillGaps } from 'utils/metrics';

import { WorkloadDetailsContext, WorkloadDetailsState } from './_context';
import {
  getSharedMetricsDateRange,
  getWorkloadLatestAppliedRecommendation,
  RESOURCE_METRICS_GAP_FILLER,
} from './_utils';
import { makeIsNamespaceWritable } from '../_hooks/permissions/makeIsNamespaceWritable';
import {
  TimeRangeSelection,
  useCurrentWorkloadMetrics,
} from '../_hooks/useCurrentWorkloadMetrics';

type Props = {
  workloadId: string;
  timeRangeSelection?: TimeRangeSelection;
  children: ReactNode | ((state: WorkloadDetailsState) => ReactNode);
};
// todo(mike) move higher
export const WorkloadDetailsProvider = ({
  workloadId,
  timeRangeSelection,
  children,
}: Props) => {
  const { cluster } = useCluster();
  const value = useCurrentWorkloadMetrics({
    workloadId,
    timeRangeSelection,
  });

  const { data: accessContext } = useWorkloadsAccessContextQuery({
    clusterId: cluster.id,
  });

  const isNamespaceWritable = useMemo(
    () => makeIsNamespaceWritable(accessContext),
    [accessContext]
  );

  const isPredictionDate = useCallback(
    (timestamp: string) => dayjs.utc(timestamp).isAfter(dayjs().utc()),
    []
  );

  // TODO (Justas): Remove this useMemo after https://castai.atlassian.net/browse/WOOP-366
  const metrics = useMemo(() => {
    const metrics = value.metrics;
    const loadedLength = value.metricsLoadedLength!;
    if (!metrics) {
      return;
    }
    const [unit, step] = (() => {
      if (!loadedLength) {
        return ['minute', 15] as const;
      }
      // date range less or equal to 23 hours - 30s step
      if (loadedLength <= dayjs.duration(23, 'hours').asMilliseconds()) {
        return ['second', 30] as const;
      }
      // date range less or equal to 2 days - 5min step
      if (loadedLength <= dayjs.duration(2, 'days').asMilliseconds()) {
        return ['minute', 5] as const;
      }
      // date range less or equal to 6 days - 10min step
      if (loadedLength <= dayjs.duration(6, 'days').asMilliseconds()) {
        return ['minute', 10] as const;
      }
      // date range more then 6 days - 15min step
      return ['minute', 15] as const;
    })();

    const containersWithMemory = metrics.containers.map((container) => ({
      ...container,
      // clean up prediction dates for memoryGib
      memoryGib: container.memoryGib.filter(
        ({ timestamp }) => !isPredictionDate(timestamp)
      ),
    }));

    const sharedDateRange = getSharedMetricsDateRange([
      ...containersWithMemory.flatMap((container) => [
        container.cpuCores,
        container.memoryGib,
      ]),
      metrics.cpu.totalCpuCores,
      metrics.pods.podCount,
    ]);

    // Shared data bounds so CPU / Memory / HPA series share one time grid
    // (Recharts syncId aligns tooltips by index).
    const dateRange = sharedDateRange
      ? {
          from: dayjs.utc(sharedDateRange.from),
          to: dayjs.utc(sharedDateRange.to),
        }
      : undefined;

    return {
      ...metrics,
      containers: containersWithMemory.map((container) => {
        const { cpuCores, memoryGib } = container;

        return {
          ...container,
          cpuCores:
            dateRange && cpuCores.length > 1
              ? fillGaps({
                  timeSeries: cpuCores,
                  dateRange,
                  filler: RESOURCE_METRICS_GAP_FILLER,
                  fillPartial: true,
                  unit,
                  step,
                })
              : cpuCores,
          memoryGib:
            dateRange && memoryGib.length > 1
              ? fillGaps({
                  timeSeries: memoryGib,
                  dateRange,
                  filler: RESOURCE_METRICS_GAP_FILLER,
                  fillPartial: true,
                  unit,
                  step,
                })
              : memoryGib,
        };
      }),
      cpu: {
        ...metrics.cpu,
        totalCpuCores:
          dateRange && metrics.cpu.totalCpuCores.length > 1
            ? fillGaps({
                timeSeries: metrics.cpu.totalCpuCores,
                dateRange,
                filler: { value: 0 },
                unit,
                step,
              })
            : metrics.cpu.totalCpuCores,
      },
      pods: {
        ...metrics.pods,
        podCount:
          dateRange && metrics.pods.podCount.length > 1
            ? fillGaps({
                timeSeries: metrics.pods.podCount,
                dateRange,
                filler: { value: 0 },
                unit,
                step,
              })
            : metrics.pods.podCount,
      },
    };
  }, [isPredictionDate, value.metrics, value.metricsLoadedLength]);

  const policiesValue = useWorkloadScalingPolicies();

  const workload = useMemo(() => {
    if (value.workload && policiesValue.policies) {
      const workload = prepareWorkload(value.workload, policiesValue.policies);

      workload.recommendation.latest = getWorkloadLatestAppliedRecommendation(
        value.pods
      );

      return workload;
    }
    return undefined;
  }, [value.workload, value.pods, policiesValue.policies]);

  return (
    <WorkloadDetailsContext.Provider
      value={{
        ...value,
        metrics,
        verticalScalingPolicies: policiesValue,
        workload,
        isNamespaceWritable,
      }}
    >
      {typeof children === 'function' ? (
        <WorkloadDetailsContext.Consumer>
          {children}
        </WorkloadDetailsContext.Consumer>
      ) : (
        <>{children}</>
      )}
    </WorkloadDetailsContext.Provider>
  );
};
